import { useMemo, useRef, useState } from 'react';

import { type QueryOptions } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { Alert, Avatar, Snackbar } from '@mui/material';
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  type GridColDef,
  type GridRowsProp,
} from '@mui/x-data-grid-premium';
import { Image } from '@realadvisor/image';
import { captureException } from '@sentry/react';
import { Search, UserIcon } from 'lucide-react';
import { Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';

import {
  type GetUsersListQuery,
  Order_By,
  type Users_Bool_Exp,
  type Users_Order_By,
} from '@graphql/__generated__/graphql';
import { AddUserModal } from '@modules/_shared/components/AddUserModal';
import { RaDataGrid } from '@modules/_shared/components/data-grid/RaDataGrid';
import { SelectStartIcon } from '@modules/_shared/components/form/SelectStartIcon';
import { ListToolbar } from '@modules/_shared/components/list-toolbar/ListToolbar';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import { SmartListTitle } from '@modules/_shared/components/SmartListTitle';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { UserSelect } from '@modules/_shared/components/user-select/UserSelect';
import { useBulkEditMode } from '@modules/_shared/hooks/useBulkEditMode';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { getIncludedIds } from '@modules/_shared/utils/gridRowSelection';
import {
  parseOrderByParam,
  withTiebreaker,
} from '@modules/_shared/utils/listUrlParams';
import { getPaginationVariables } from '@modules/_shared/utils/paginations';
import { prepareWhereClauseQuery } from '@modules/_shared/utils/parseWhereClause';
import { BulkAccountManagerReassignDialog } from '@modules/users/components/BulkAccountManagerReassignDialog';
import { OnboardingStatusChip } from '@modules/users/components/OnboardingStatusChip';
import { UsersBulkActions } from '@modules/users/components/UsersBulkActions';
import { UserDrawer } from '@modules/users/UserDrawer';
import {
  DELETE_USER,
  GET_USERS_LIST,
  GET_USERS_LIST_COUNT,
} from '@modules/users/userQueries';
import { UsersExport } from '@modules/users/UsersExport';
import { UsersImport } from '@modules/users/UsersImport';
import { useLocale } from '@shared/hooks/locale';

import { deleteUsersPreservingHistory } from './deleteUsersPreservingHistory';

type User = GetUsersListQuery['users'][number];

export const initialUsersListQuery = (
  searchParams: URLSearchParams,
): QueryOptions => {
  const where: Users_Bool_Exp = prepareWhereClauseQuery(
    JSON.parse(searchParams.get('where') || '{}'),
  );
  const defaultFilters: Users_Bool_Exp = {
    is_deleted: { _eq: false },
  };
  const order_by = withTiebreaker<Users_Order_By>(
    parseOrderByParam<Users_Order_By>(searchParams.get('order_by'), [
      { created_at: Order_By.Desc },
    ]),
    { id: Order_By.Asc },
    'id',
  );
  const { limit, offset } = getPaginationVariables(searchParams);

  return {
    query: GET_USERS_LIST,
    variables: {
      where: { ...defaultFilters, ...where },
      order_by,
      limit,
      offset,
    },
  };
};

export const Users = () => {
  const [searchParams] = useSearchParams();
  const { editMode, selectedRows, setSelectedRows } = useBulkEditMode();
  const includedSelectedRows = getIncludedIds(selectedRows);
  const navigate = useNavigate();
  const { t } = useLocale();
  const { me } = useAppData();
  const [reassignUsers, setReassignUsers] = useState<User[] | null>(null);
  const [reassignedCount, setReassignedCount] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const deleteInProgress = useRef(false);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const canDelete = me?.is_admin === true && me.can_edit_settings === true;

  const { variables } = useMemo(
    () => initialUsersListQuery(searchParams),
    [searchParams],
  );
  const { where, order_by, limit, offset } = variables ?? {};

  const userLink = (id: string) =>
    ({
      pathname: id,
      search: searchParams.toString(),
    } as const);

  const { data, loading, error, refetch } = useQuery(GET_USERS_LIST, {
    variables: {
      where,
      order_by,
      limit,
      offset,
    },
  });
  const users: GridRowsProp<User> = data?.users ?? [];
  const selectedUsers = users.filter(user =>
    includedSelectedRows?.includes(user.id),
  );

  const { countLimit } = getPaginationVariables(searchParams, undefined, true);
  const { data: countData, refetch: refetchCount } = useQuery(
    GET_USERS_LIST_COUNT,
    {
      variables: {
        where,
        limit: countLimit,
      },
    },
  );

  const [deleteUser] = useMutation(DELETE_USER, { errorPolicy: 'none' });

  const handleDelete = async (ids: string[]) => {
    if (!canDelete || deleteInProgress.current || ids.length === 0) {
      return { failedCount: 0 };
    }
    deleteInProgress.current = true;
    setDeleting(true);
    setRefreshFailed(false);
    try {
      const result = await deleteUsersPreservingHistory(
        ids,
        id => deleteUser({ variables: { user_id: id } }),
        async () => {
          const results = await Promise.allSettled([refetch(), refetchCount()]);
          if (results.some(result => result.status === 'rejected')) {
            throw new Error('User list refresh failed');
          }
        },
      );
      for (const { id, error } of result.failures) {
        console.error(error);
        captureException(error, {
          tags: { feature: 'users-bulk-delete' },
          extra: { userId: id },
        });
      }
      if (result.refreshFailed) {
        console.error(result.refreshError);
        captureException(result.refreshError, {
          tags: { feature: 'users-bulk-delete', step: 'refresh' },
        });
      }
      setSelectedRows({ type: 'include', ids: new Set(result.failedIds) });
      setRefreshFailed(result.refreshFailed);
      return { failedCount: result.failedIds.length };
    } finally {
      deleteInProgress.current = false;
      setDeleting(false);
    }
  };

  if (error) {
    return <Alert severity="error">{JSON.stringify(error)}</Alert>;
  }

  const columns: GridColDef<User>[] = [
    {
      field: 'created_at',
      headerName: t('Created'),
      width: 75,
      renderCell: ({ value }) => <TimeAgo dateString={value} />,
    },
    {
      field: 'avatar',
      headerName: '',
      width: 50,
      sortable: false,
      filterable: false,
      display: 'flex',
      renderCell: ({ row }) => {
        const imageUrl = row?.user_images?.[0]?.image?.url;
        return (
          <Avatar>
            {imageUrl != null ? (
              <Image src={imageUrl} srcSize={[[40, 40]]} />
            ) : (
              [
                row.first_name?.charAt(0) ?? null,
                row.last_name?.charAt(0) ?? null,
              ].join('') ?? <UserIcon size={18} />
            )}
          </Avatar>
        );
      },
    },
    {
      field: 'first_name',
      headerName: t('First name'),
      width: 100,
    },
    {
      field: 'last_name',
      headerName: t('Last name'),
      width: 150,
    },
    {
      field: 'account_manager.full_name',
      headerName: t('accountManager'),
      width: 200,
      valueGetter: (_, row) => row.account_manager?.full_name ?? '',
    },
    {
      field: 'emails',
      headerName: t('Emails'),
      width: 250,
      valueGetter: (_, row) => {
        return [...row.emails]
          .sort((a, b) => Number(b.primary) - Number(a.primary))
          .map(({ email }) => email)
          .join(', ');
      },
    },
    {
      field: 'phone_numbers',
      headerName: t('Phone numbers'),
      width: 150,
      valueGetter: (_, row) => {
        return [...row.phone_numbers]
          .sort((a, b) => Number(b.primary) - Number(a.primary))
          .map(({ number }) => number)
          .join(', ');
      },
    },
    {
      field: 'onboarding_status',
      headerName: t('Onboarding status'),
      width: 160,
      renderCell: ({ value }) => <OnboardingStatusChip status={value} />,
    },
  ];

  return (
    <>
      <SmartListTitle tableName="users" defaultTitle={t('Users')} />
      <Snackbar open={refreshFailed} onClose={() => setRefreshFailed(false)}>
        <Alert severity="error" variant="filled" sx={{ width: '100%' }}>
          {t(
            'The user list could not be refreshed. Reload the page to see the latest results.',
          )}
        </Alert>
      </Snackbar>
      <ListToolbar
        tableName="users"
        refetch={() => {
          void Promise.allSettled([refetch(), refetchCount()]);
        }}
        quickFilters={[
          ...(me?.is_admin
            ? [
                {
                  label: t('accountManager'),
                  path: ['users_bool_exp', 'account_manager'],
                },
              ]
            : []),
          {
            label: t('Created at'),
            path: ['users_bool_exp', 'created_at'],
          },
          ...(me?.is_admin
            ? [
                {
                  label: t('Subscription status'),
                  path: ['users_bool_exp', 'subscriptions', 'status', '_in'],
                },
                {
                  label: t('Show in agency pages'),
                  path: ['users_bool_exp', 'show_in_agency_pages'],
                },
                {
                  label: t('Onboarding status'),
                  path: ['users_bool_exp', 'onboarding_status', '_in'],
                },
              ]
            : []),
        ]}
        searchComponent={
          <UserSelect
            autoFocus
            clearOnSelect
            InputProps={{
              startAdornment: <SelectStartIcon icon={Search} />,
            }}
            sx={{ maxWidth: 350, flexGrow: 1, ml: 0 }}
            onChange={userId => {
              if (!userId) {
                return;
              }
              navigate(userLink(userId));
            }}
          />
        }
        newLink="new"
        ImportDialog={UsersImport}
        ExportDialog={UsersExport}
        bulkActions={
          me?.is_admin && (
            <UsersBulkActions
              users={selectedUsers}
              selectedIds={includedSelectedRows}
              loading={loading}
              canDelete={canDelete}
              deleting={deleting}
              onReassign={setReassignUsers}
              onDelete={handleDelete}
            />
          )
        }
        showRealCountOption
      />
      <RaDataGrid
        columns={columns}
        rows={users}
        rowCount={countData?.users_aggregate?.aggregate?.count ?? 0}
        approximateRowCount
        onRowClick={({ id }) => {
          if (!editMode) {
            navigate(userLink(id as string));
          }
        }}
        loading={loading}
        checkboxSelection={editMode}
        disableRowSelectionOnClick={!editMode}
        keepNonExistentRowsSelected
        onRowSelectionModelChange={newSelection => {
          if (!deleteInProgress.current) {
            setSelectedRows(newSelection);
          }
        }}
        rowSelectionModel={selectedRows}
        pinnedColumns={{
          left: [GRID_CHECKBOX_SELECTION_COL_DEF.field],
        }}
        columnVisibilityModel={{
          [GRID_CHECKBOX_SELECTION_COL_DEF.field]: editMode,
          'account_manager.full_name': me?.is_admin === true,
          onboarding_status: me?.is_admin === true,
        }}
      />
      {reassignUsers != null && (
        <BulkAccountManagerReassignDialog
          users={reassignUsers}
          onClose={() => setReassignUsers(null)}
          onReassigned={count => {
            setReassignUsers(null);
            setSelectedRows({ type: 'include', ids: new Set() });
            setReassignedCount(count);
            void Promise.allSettled([refetch(), refetchCount()]);
          }}
        />
      )}
      {reassignedCount != null && (
        <RASnackbar
          severity="success"
          autoHideDuration={6000}
          message={t('Account manager reassigned for {{count}} users.', {
            count: reassignedCount,
          })}
          onClose={() => setReassignedCount(null)}
        />
      )}
      <Routes>
        <Route
          path="new"
          element={
            <AddUserModal
              opened
              onClose={user => {
                navigate(user == null ? '.' : userLink(user.id));
              }}
              refetchQueries={[
                {
                  query: GET_USERS_LIST,
                  variables: {
                    where,
                    order_by,
                    limit,
                    offset,
                  },
                },
              ]}
            />
          }
        />
        <Route path=":userId/*" element={<UserDrawer />} />
      </Routes>
    </>
  );
};

export default Users;
